import React from 'react';
import { motion } from 'framer-motion';
import { Grid, List, Download, Share2, Trash2 } from 'lucide-react';
import { Document } from '../../types';
import { useDocumentStore } from '../../store/documentStore';
import { useDocuments } from '../../hooks/useDocuments';
import { useResponsive } from '../../hooks/useResponsive';
import { ResponsiveGrid } from '../molecules/ResponsiveContainer';
import DocumentCard from '../molecules/DocumentCard';
import Button from '../atoms/Button';
import Card from '../atoms/Card';
import toast from 'react-hot-toast';

interface DocumentGridProps {
  documents: Document[];
  onDocumentClick?: (document: Document) => void;
}

const DocumentGrid: React.FC<DocumentGridProps> = ({
  documents,
  onDocumentClick,
}) => {
  const {
    selectedDocuments,
    viewMode,
    toggleDocumentSelection,
    selectAllDocuments,
    clearSelection,
    setViewMode,
  } = useDocumentStore();

  const { deleteDocument } = useDocuments();
  const { isMobile, isTablet, getColumns } = useResponsive();

  const handleBulkAction = async (action: 'download' | 'share' | 'delete') => {
    if (selectedDocuments.length === 0) {
      toast.error('No documents selected');
      return;
    }

    if (action === 'delete') {
      const confirmed = window.confirm(
        `Are you sure you want to delete ${selectedDocuments.length} document${selectedDocuments.length !== 1 ? 's' : ''}? This action cannot be undone.`
      );
      
      if (confirmed) {
        try {
          // Delete each selected document
          for (const documentId of selectedDocuments) {
            const document = documents.find(doc => doc.id === documentId);
            await deleteDocument(documentId, document?.name);
          }
          
          // Clear selection after successful deletion
          clearSelection();
          toast.success(`${selectedDocuments.length} document${selectedDocuments.length !== 1 ? 's' : ''} deleted successfully`);
        } catch (error) {
          console.error('Bulk delete failed:', error);
          toast.error('Failed to delete some documents');
        }
      }
    } else {
      console.log(`Performing ${action} on documents:`, selectedDocuments);
      toast.info(`${action} action triggered for ${selectedDocuments.length} documents`);
      // Implement other bulk actions
    }
  };

  const handleNotarizationComplete = (documentId: string, transactionId: string) => {
    toast.success(`Document ${documentId} successfully notarized!`, {
      duration: 5000,
      icon: '🎉'
    });
    console.log(`Document ${documentId} notarized with transaction: ${transactionId}`);
  };

  // Responsive grid columns
  const gridColumns = getColumns({
    mobile: 1,
    tablet: 2,
    desktop: viewMode === 'grid' ? 3 : 1
  });

  if (documents.length === 0) {
    return (
      <Card className="py-8 lg:py-12">
        <div className="text-center space-y-4">
          <div className="mx-auto w-12 h-12 lg:w-16 lg:h-16 bg-gray-100 rounded-full flex items-center justify-center">
            <Grid className="h-6 w-6 lg:h-8 lg:w-8 text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">No documents found</h3>
            <p className="text-gray-600 text-sm lg:text-base">Upload your first document to get started</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <p className="text-sm text-gray-600">
            {documents.length} document{documents.length !== 1 ? 's' : ''}
            {selectedDocuments.length > 0 && (
              <span className="ml-2 font-medium">
                ({selectedDocuments.length} selected)
              </span>
            )}
          </p>

          {selectedDocuments.length > 0 && !isMobile && (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost" 
                size="sm" 
                icon={Download} 
                onClick={() => handleBulkAction('download')}
              >
                Download
              </Button>
              <Button
                variant="ghost" 
                size="sm" 
                icon={Share2} 
                onClick={() => handleBulkAction('share')}
              >
                Share
              </Button>
              <Button
                variant="ghost" 
                size="sm" 
                icon={Trash2} 
                onClick={() => handleBulkAction('delete')}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Delete
              </Button>
              <Button
                variant="outline" 
                size="sm"
                onClick={clearSelection}
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* View Mode Toggle - Hidden on mobile */}
        {!isMobile && (
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'ghost'}
              size="sm"
              icon={Grid}
              onClick={() => setViewMode('grid')}
            />
            <Button
              variant={viewMode === 'list' ? 'primary' : 'ghost'}
              size="sm"
              icon={List}
              onClick={() => setViewMode('list')}
            />
          </div>
        )}
      </div>

      {/* Mobile Bulk Actions */}
      {selectedDocuments.length > 0 && isMobile && (
        <Card className="p-4">
          <div className="flex gap-2">
            <Button
              variant="ghost" 
              size="sm" 
              icon={Download} 
              onClick={() => handleBulkAction('download')}
              className="flex-1"
            >
              Download
            </Button>
            <Button
              variant="ghost" 
              size="sm" 
              icon={Share2} 
              onClick={() => handleBulkAction('share')}
              className="flex-1"
            >
              Share
            </Button>
            <Button
              variant="ghost" 
              size="sm" 
              icon={Trash2} 
              onClick={() => handleBulkAction('delete')}
              className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Delete
            </Button>
          </div>
          <Button
            variant="outline" 
            size="sm"
            onClick={clearSelection}
            className="w-full mt-2"
          >
            Clear Selection
          </Button>
        </Card>
      )}

      {/* Selection Controls */}
      {documents.length > 0 && (
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={selectedDocuments.length === documents.length ? clearSelection : selectAllDocuments}
          >
            {selectedDocuments.length === documents.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
      )}

      {/* Document Grid/List */}
      <motion.div layout>
        <ResponsiveGrid
          columns={{
            sm: 1,
            md: isTablet ? 2 : 2,
            lg: viewMode === 'grid' ? 3 : 1,
            xl: viewMode === 'grid' ? 4 : 1
          }}
          gap={isMobile ? 'sm' : 'md'}
        >
          {documents.map((document, index) => (
            <motion.div
              key={`document-${document.id}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              layout
            >
              <DocumentCard
                document={document}
                selected={selectedDocuments.includes(document.id)}
                onSelect={() => toggleDocumentSelection(document.id)}
                onNotarizationComplete={(transactionId) => 
                  handleNotarizationComplete(document.id, transactionId)
                }
              />
            </motion.div>
          ))}
        </ResponsiveGrid>
      </motion.div>
    </div>
  );
};

export default DocumentGrid;